/**
 * parseLetter — extrait les métadonnées d'une lettre administrative française
 * générée par Zerux, pour l'affichage dans le Coffre (aperçu + auto-nommage).
 *
 * Retourne : { title, object, recipient, date, excerpt }
 * Ne modifie jamais le texte de la lettre.
 */

const ORGANISMS = [
  { key: "CAF", label: "CAF" },
  { key: "CPAM", label: "CPAM" },
  { key: "MSA", label: "MSA" },
  { key: "URSSAF", label: "URSSAF" },
  { key: "IMPÔTS", label: "Impôts" },
  { key: "DGFIP", label: "Impôts" },
  { key: "FINANCES PUBLIQUES", label: "Impôts" },
  { key: "TRÉSOR PUBLIC", label: "Trésor Public" },
  { key: "FRANCE TRAVAIL", label: "France Travail" },
  { key: "PÔLE EMPLOI", label: "France Travail" },
  { key: "CARSAT", label: "CARSAT" },
  { key: "CNAV", label: "CNAV" },
  { key: "MDPH", label: "MDPH" },
  { key: "PRÉFECTURE", label: "Préfecture" },
  { key: "MAIRIE", label: "Mairie" },
  { key: "BAILLEUR", label: "Bailleur" },
  { key: "BANQUE", label: "Banque" },
  { key: "ASSURANCE", label: "Assurance" },
  { key: "MUTUELLE", label: "Mutuelle" },
  { key: "ÉCOLE", label: "École" },
  { key: "EMPLOYEUR", label: "Employeur" },
  { key: "OFFICIER DU MINISTÈRE PUBLIC", label: "Amende" },
];

const TYPE_KEYWORDS = [
  { keys: ["résilier", "résiliation"], label: "Résiliation" },
  { keys: ["contester", "contestation"], label: "Contestation" },
  { keys: ["attestation"], label: "Attestation" },
  { keys: ["demande de délai", "délai de paiement", "échéancier"], label: "Délai de paiement" },
  { keys: ["remboursement"], label: "Remboursement" },
  { keys: ["absence"], label: "Absence" },
  { keys: ["changement d'adresse", "changement d’adresse", "nouvelle adresse"], label: "Changement d'adresse" },
  { keys: ["recours"], label: "Recours" },
  { keys: ["signalement"], label: "Signalement" },
  { keys: ["réclamation"], label: "Réclamation" },
  { keys: ["mise en demeure"], label: "Mise en demeure" },
  { keys: ["subvention"], label: "Subvention" },
  { keys: ["relance"], label: "Relance" },
  { keys: ["congé"], label: "Congé" },
  { keys: ["démission"], label: "Démission" },
];

function stripAccents(str) {
  return (str || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
}

function detectOrganism(text) {
  const T = stripAccents(text).toUpperCase();
  for (const o of ORGANISMS) {
    if (T.includes(stripAccents(o.key).toUpperCase())) return o.label;
  }
  return null;
}

function detectType(text) {
  const T = (text || "").toLowerCase();
  for (const t of TYPE_KEYWORDS) {
    if (t.keys.some((k) => T.includes(k))) return t.label;
  }
  return null;
}

function todayFR() {
  const d = new Date();
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${d.getFullYear()}`;
}

function parseFrenchDate(str) {
  // "20 août 2026" -> "20/08/2026"
  if (!str) return null;
  const months = {
    janvier: "01", février: "02", fevrier: "02", mars: "03", avril: "04",
    mai: "05", juin: "06", juillet: "07", août: "08", aout: "08",
    septembre: "09", octobre: "10", novembre: "11", décembre: "12", decembre: "12",
  };
  const m = str.trim().toLowerCase().match(/^(\d{1,2})\s+([a-zéû]+)\s+(\d{4})/i);
  if (!m) return null;
  const day = m[1].padStart(2, "0");
  const month = months[stripAccents(m[2])];
  if (!month) return null;
  return `${day}/${month}/${m[3]}`;
}

export function parseLetter(letter) {
  const text = (letter || "").trim();
  if (!text) {
    return {
      title: `Lettre – ${todayFR()}`,
      object: "",
      recipient: "",
      date: todayFR(),
      excerpt: "",
    };
  }

  const lines = text.split("\n").map((l) => l.trim());

  // --- Objet ---
  const objetLine = lines.find((l) => /^objet\s*:/i.test(l));
  const object = objetLine ? objetLine.replace(/^objet\s*:\s*/i, "").trim() : "";

  // --- Date + ville ---
  const faitLine = lines.find((l) => /^fait\s+à/i.test(l));
  let date = todayFR();
  if (faitLine) {
    const m = faitLine.match(/le\s+(.+?)[.\n]/i);
    if (m) {
      const parsed = parseFrenchDate(m[1]);
      if (parsed) date = parsed;
    }
  }

  // --- Destinataire : bloc juste avant "Fait à …", séparé par des lignes vides ---
  // Heuristique : on cherche les 1-4 premières lignes qui contiennent un organisme
  let recipient = "";
  const orgFromContent = detectOrganism(text);
  if (orgFromContent) {
    // Recherche du bloc destinataire dans les 15 premières lignes
    const firstBlock = lines.slice(0, 20);
    const idx = firstBlock.findIndex((l) => {
      const up = stripAccents(l).toUpperCase();
      return ORGANISMS.some((o) => up.includes(stripAccents(o.key).toUpperCase()));
    });
    if (idx >= 0) {
      recipient = firstBlock[idx];
    } else {
      recipient = orgFromContent;
    }
  }

  // --- Excerpt : 2 premières lignes du corps (après "Madame, Monsieur,") ---
  let excerpt = "";
  const salutIdx = lines.findIndex((l) => /^madame,?\s*monsieur/i.test(l));
  if (salutIdx >= 0) {
    const body = lines.slice(salutIdx + 1).filter((l) => l.length > 0);
    excerpt = body.slice(0, 2).join(" ").slice(0, 200);
  } else {
    excerpt = text.slice(0, 200).replace(/\n+/g, " ");
  }

  // --- Titre auto : [Organisme] – [Type] – [Date] ---
  const org = orgFromContent || "Courrier";
  const type = detectType(object) || detectType(text) || "Lettre";
  const title = `${org} – ${type} – ${date}`;

  return { title, object, recipient, date, excerpt };
}
